import { Autocomplete, Box, Button, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useDrawer } from 'components/app/DrawerContext';
import ConfirmationDialog from 'commonComponents/ConfirmationDialog';
import { BuyRequestsPanel } from './groupReport/BuyRequestsPanel';
import { GroupMembersTable } from './groupReport/GroupMembersTable';
import { GroupReportHeader } from './groupReport/GroupReportHeader';
import { useGroupReport } from './groupReport/useGroupReport';
import { GroupMemberRow } from './groupReport/types';

function createDraftOrders(memberEmails: string[], currentMembers: Array<{ memberEmail: string; order: number }>) {
  return memberEmails.reduce<Record<string, string>>((result, memberEmail) => {
    const current = currentMembers.find((member) => member.memberEmail === memberEmail);
    result[memberEmail] = String(current?.order || 1);
    return result;
  }, {});
}

function validateOrderMap(memberCount: number, orderMap: Record<string, string>): string | null {
  const orders = Object.values(orderMap).map((value) => Number(value));

  if (orders.some((order) => !Number.isInteger(order))) return 'All order fields must contain whole numbers.';
  if (orders.some((order) => order < 1 || order > memberCount)) return `Order numbers must be between 1 and ${memberCount}.`;
  if (new Set(orders).size !== orders.length) return 'Each member must have a unique order number.';

  return null;
}

export default function GroupReport() {
  const { fundManagementAdminRight } = useDrawer();
  const {
    members,
    buyRequests,
    memberSuggestions,
    loadingSuggestions,
    suggestionsError,
    loading,
    error,
    permissionDenied,
    loadingOrderChecked,
    refresh,
    loadMemberSuggestions,
    reviewRequest,
    toggleLedgerApproval,
    toggleOrderChecked: toggleOrderCheckedAction,
    addMember,
    removeMember,
    reorderMembers
  } = useGroupReport();
  const [query, setQuery] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [draftOrders, setDraftOrders] = useState<Record<string, string>>({});
  const [orderError, setOrderError] = useState<string | null>(null);
  const [showRequests, setShowRequests] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [suggestionInput, setSuggestionInput] = useState('');
  const [requestActionLoadingId, setRequestActionLoadingId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pendingDeleteMemberEmail, setPendingDeleteMemberEmail] = useState<string | null>(null);
  const [deletingMember, setDeletingMember] = useState(false);

  const [approvedMemberEmails, setApprovedMemberEmails] = useState<string[]>([]);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const searchCacheRef = useRef<Record<string, any[]>>({});

  const filteredMembers = useMemo(() => {
    const search = query.toLowerCase().trim();
    if (!search) return members;
    return members.filter((member) => member.memberName.toLowerCase().includes(search));
  }, [query, members]);

  const canManageMembers = fundManagementAdminRight && !permissionDenied;

  useEffect(() => {
    setDraftOrders(createDraftOrders(members.map((member) => member.memberEmail), members));
  }, [members]);

  useEffect(() => {
    const stored = localStorage.getItem('groupReportApprovedMembers');
    const storedApprovals = stored ? (JSON.parse(stored) as string[]) : [];
    const backendApprovals = members.filter((member) => member.isApproved).map((member) => member.memberEmail);
    const merged = Array.from(new Set([...storedApprovals, ...backendApprovals]));
    setApprovedMemberEmails(merged);
  }, [members]);

  useEffect(() => {
    if (!showAddDialog) {
      setSuggestionInput('');
      searchCacheRef.current = {};
    }
  }, [showAddDialog]);

  const startEditMode = () => {
    setDraftOrders(createDraftOrders(members.map((member) => member.memberEmail), members));
    setOrderError(null);
    setIsEditMode(true);
  };

  const cancelEditMode = () => {
    setDraftOrders(createDraftOrders(members.map((member) => member.memberEmail), members));
    setOrderError(null);
    setIsEditMode(false);
  };

  const saveOrderChanges = async () => {
    const validationError = validateOrderMap(members.length, draftOrders);
    if (validationError) {
      setOrderError(validationError);
      return;
    }

    const orderUpdates = members.map((member) => ({
      memberEmail: member.memberEmail,
      order: Number(draftOrders[member.memberEmail])
    }));

    try {
      await reorderMembers(orderUpdates);
      setIsEditMode(false);
      setOrderError(null);
      await refresh();
    } catch (caughtError) {
      setOrderError(caughtError instanceof Error ? caughtError.message : 'Failed to save member order.');
    }
  };

  const toggleOrder = async (member: GroupMemberRow) => {
    const isCurrentlyApproved = approvedMemberEmails.includes(member.memberEmail) || !!member.isApproved;
    const nextApproved = !isCurrentlyApproved;

    setApprovedMemberEmails((current) => {
      const next = nextApproved
        ? Array.from(new Set([...current, member.memberEmail]))
        : current.filter((email) => email !== member.memberEmail);
      localStorage.setItem('groupReportApprovedMembers', JSON.stringify(next));
      return next;
    });

    if (member.expenseId && member.transactionDate) {
      try {
        await toggleLedgerApproval({
          expenseId: member.expenseId,
          transactionDate: member.transactionDate,
          isApproved: nextApproved,
          unitType: 'incomeUnits'
        });
      } catch (caughtError) {
        setSaveError(caughtError instanceof Error ? caughtError.message : 'Failed to update approval state.');
      }
    }
  };

  const handleToggleOrderChecked = async (member: GroupMemberRow) => {
    try {
      await toggleOrderCheckedAction(member);
    } catch (caughtError) {
      setSaveError(caughtError instanceof Error ? caughtError.message : 'Failed to update order checked status.');
    }
  };

  const handleApproveJoinRequest = async (requestId: string) => {
    if (!canManageMembers) {
      setSaveError('Only admin can approve requests.');
      return;
    }

    try {
      setRequestActionLoadingId(requestId);
      await reviewRequest({ requestId, action: 'approved' });
      await refresh();
    } catch (caughtError) {
      setSaveError(caughtError instanceof Error ? caughtError.message : 'Failed to approve request.');
    } finally {
      setRequestActionLoadingId(null);
    }
  };

  const handleRejectJoinRequest = async (requestId: string) => {
    if (!canManageMembers) {
      setSaveError('Only admin can reject requests.');
      return;
    }

    try {
      setRequestActionLoadingId(requestId);
      await reviewRequest({ requestId, action: 'rejected' });
      await refresh();
    } catch (caughtError) {
      setSaveError(caughtError instanceof Error ? caughtError.message : 'Failed to reject request.');
    } finally {
      setRequestActionLoadingId(null);
    }
  };

  const handleRemoveMember = async (memberEmail: string) => {
    setPendingDeleteMemberEmail(memberEmail);
  };

  const handleConfirmRemoveMember = async () => {
    if (!pendingDeleteMemberEmail) return;

    try {
      setDeletingMember(true);
      await removeMember(pendingDeleteMemberEmail);
      setApprovedMemberEmails((current) => current.filter((email) => email !== pendingDeleteMemberEmail));
      await refresh();
      setPendingDeleteMemberEmail(null);
    } catch (caughtError) {
      setSaveError(caughtError instanceof Error ? caughtError.message : 'Failed to remove member.');
    } finally {
      setDeletingMember(false);
    }
  };

  const handleAddMember = async () => {
    if (!newMemberName.trim() || !newMemberEmail.trim()) {
      setSaveError('Member name and email are required.');
      return;
    }

    try {
      await addMember({
        memberName: newMemberName.trim(),
        memberEmail: newMemberEmail.trim()
      });

      setShowAddDialog(false);
      setNewMemberName('');
      setNewMemberEmail('');
      setSuggestionInput('');
      searchCacheRef.current = {};
      setSaveError(null);
    } catch (caughtError) {
      setSaveError(caughtError instanceof Error ? caughtError.message : 'Failed to add member.');
    }
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        minHeight: '100vh',
        px: { xs: 1.5, sm: 3 },
        py: 3,
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4edf5 100%)'
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <GroupReportHeader
          title="Group Report"
          subtitle={canManageMembers ? 'Manage group members and requests' : 'Review your group report and requests'}
          query={query}
          requestCount={buyRequests.length}
          canManageMembers={canManageMembers}
          isEditMode={isEditMode}
          onQueryChange={setQuery}
          onOpenAddMember={() => setShowAddDialog(true)}
          onToggleRequests={() => setShowRequests(true)}
          onStartEdit={startEditMode}
          onCancelEdit={cancelEditMode}
          onSaveEdit={saveOrderChanges}
        />

        {loading && (
          <Box sx={{ mb: 2 }}>
            <Chip
              label="Loading group data..."
              sx={{
                width: '100%',
                justifyContent: 'flex-start',
                borderRadius: 2,
                px: 1,
                py: 3,
                backgroundColor: '#eff6ff',
                border: '1px solid #bfdbfe',
                color: '#1d4ed8',
                fontWeight: 600
              }}
            />
          </Box>
        )}

        {error && (
          <Box sx={{ mb: 2 }}>
            <Chip
              label={error}
              sx={{
                width: '100%',
                justifyContent: 'flex-start',
                borderRadius: 2,
                px: 1,
                py: 3,
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#dc2626',
                fontWeight: 600
              }}
            />
          </Box>
        )}

        {saveError && (
          <Box sx={{ mb: 2 }}>
            <Chip
              label={saveError}
              sx={{
                width: '100%',
                justifyContent: 'flex-start',
                borderRadius: 2,
                px: 1,
                py: 3,
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#dc2626',
                fontWeight: 600
              }}
            />
          </Box>
        )}

        {isEditMode && canManageMembers && (
          <Box sx={{ mb: 2 }}>
            <Chip
              label="Edit mode is active. Type each member's new order number, then click Save Changes."
              sx={{
                width: '100%',
                justifyContent: 'flex-start',
                borderRadius: 2,
                px: 1,
                py: 3,
                backgroundColor: '#fffbeb',
                border: '1px solid #fcd34d',
                color: '#b45309',
                fontWeight: 600
              }}
            />
          </Box>
        )}

        <GroupMembersTable
          members={members}
          filteredMembers={filteredMembers}
          loading={loading}
          isEditMode={isEditMode}
          canManageMembers={canManageMembers}
          draftOrders={draftOrders}
          selectedOrders={approvedMemberEmails}
          orderError={orderError}
          onToggleOrder={toggleOrder}
          onDraftOrderChange={(memberEmail, value) => {
            setDraftOrders((current) => ({ ...current, [memberEmail]: value }));
            setOrderError(null);
          }}
          onRemoveMember={handleRemoveMember}
          onToggleOrderChecked={handleToggleOrderChecked}
          loadingOrderChecked={loadingOrderChecked}
        />

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="text" onClick={() => void refresh()} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Refresh data
          </Button>
        </Box>

        <BuyRequestsPanel
          open={showRequests}
          requests={buyRequests}
          canManageMembers={canManageMembers}
          actionLoadingId={requestActionLoadingId}
          onApprove={handleApproveJoinRequest}
          onReject={handleRejectJoinRequest}
          onClose={() => setShowRequests(false)}
        />

        <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} fullWidth maxWidth="sm">
          <DialogTitle sx={{ fontWeight: 800 }}>Add Member</DialogTitle>
          <DialogContent sx={{ display: 'grid', gap: 2, pt: 1 }}>
            <Autocomplete
              options={memberSuggestions}
              getOptionLabel={(option) => 
                typeof option === 'string' 
                  ? option 
                  : `${option.memberName} (${option.memberEmail})`
              }
              isOptionEqualToValue={(option, value) => 
                typeof option === 'string' || typeof value === 'string'
                  ? option === value
                  : option.memberEmail === value.memberEmail
              }
              loading={loadingSuggestions}
              inputValue={suggestionInput}
              open={suggestionInput.length >= 3}
              noOptionsText={suggestionInput.length < 3 ? '' : 'No members found'}
              onInputChange={(_, value) => {
                setSuggestionInput(value);
                
                if (debounceTimerRef.current) {
                  clearTimeout(debounceTimerRef.current);
                }
                
                if (value.length < 3) {
                  return;
                }
                
                if (searchCacheRef.current[value]) {
                  memberSuggestions !== searchCacheRef.current[value] && void loadMemberSuggestions(value);
                  return;
                }
                
                debounceTimerRef.current = setTimeout(() => {
                  void loadMemberSuggestions(value);
                }, 700);
              }}
              onChange={(_, selectedOption) => {
                if (!selectedOption) return;
                if (typeof selectedOption === 'string') return;
                setNewMemberName(selectedOption.memberName);
                setNewMemberEmail(selectedOption.memberEmail);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search member"
                  helperText={suggestionInput.length < 3 ? 'Type at least 3 characters to search' : suggestionsError || 'Select a user to auto-fill details.'}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingSuggestions ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
              disabled={!canManageMembers}
              freeSolo
            />
            <TextField label="Member name" value={newMemberName} onChange={(event) => setNewMemberName(event.target.value)} fullWidth />
            <TextField label="Member email" value={newMemberEmail} onChange={(event) => setNewMemberEmail(event.target.value)} fullWidth />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={() => setShowAddDialog(false)} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleAddMember()}
              sx={{ textTransform: 'none' }}
              disabled={!canManageMembers}
            >
              Add Member
            </Button>
          </DialogActions>
        </Dialog>

        <ConfirmationDialog
          open={!!pendingDeleteMemberEmail}
          title="Confirm Delete"
          message="Are you sure you want to delete this member?"
          confirmButtonText="Delete"
          confirmButtonColor="error"
          onConfirm={() => void handleConfirmRemoveMember()}
          onCancel={() => setPendingDeleteMemberEmail(null)}
          loading={deletingMember}
        />
      </Box>
    </Container>
  );
}
